/**
 * The account stated the way a business states a result.
 *
 * What went in, what came out, what the strategy earned, what is still open,
 * what it is all worth. Read top to bottom it answers the question an owner
 * asks about their own money and cannot answer from a rate alone: not "how
 * fast is it growing" but "where does it stand".
 *
 * Its value is that the terms add up. Deposits less withdrawals, plus what has
 * been realised, plus what was closed by hand, less what trading cost beyond
 * those results, plus what the venue has settled in funding, plus what the open
 * positions are worth, is the balance — and
 * a reader who can check that on the screen has a reason to trust every other
 * figure on the product. The sum is measured on the server for the same
 * reason: a screen adding money is a second opinion about the ledger.
 *
 * It carries no period. Every other block on this page is governed by the
 * period control; this one is a standing fact, and it says so rather than
 * sitting silently under a control that does not reach it.
 */
import type { AccountLedgerResponse } from "@/shared/api";
import { isNegative, isZero, money, signedMoney, toPlaces } from "@/shared/money/money";
import { plural } from "@/shared/text/plural";
import { PANEL_HEADER, PANEL_TITLE } from "@/design-system/Panel";

/** The day a stamp falls on, in the reader's words. */
function day(at: string): string {
  return new Date(at).toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
}

function Line({
  label,
  value,
  note,
  testId,
  tone = "plain",
  signed = false,
}: {
  readonly label: string;
  readonly value: string;
  readonly note?: string | undefined;
  readonly testId: string;
  readonly tone?: "plain" | "result" | "total";
  readonly signed?: boolean;
}): React.JSX.Element {
  const shown = signed ? signedMoney(value) : money(value);
  const colour =
    tone === "result"
      ? isNegative(toPlaces(value))
        ? "text-status-negative"
        : "text-status-positive"
      : "text-text-primary";
  return (
    <div
      className={`flex min-w-0 items-baseline justify-between gap-4 ${
        tone === "total" ? "border-t border-border-subtle pt-2" : ""
      }`}
    >
      <span className="min-w-0">
        <span className="block text-label-caps text-text-muted">{label}</span>
        {note !== undefined && (
          <span className="block text-helper-sm text-text-muted">{note}</span>
        )}
      </span>
      <span
        data-testid={testId}
        title={value}
        className={`flex shrink-0 items-baseline gap-1 tabular-nums whitespace-nowrap ${
          tone === "total" ? "text-metric-md" : "text-body-main"
        } ${colour}`}
      >
        {shown}
        <span className="text-helper-sm font-normal text-text-muted">USDT</span>
      </span>
    </div>
  );
}

function count(value: number, one: string, few: string, many: string): string {
  return `${String(value)} ${plural(value, one, few, many)}`;
}

export function AccountLedger({
  ledger,
}: {
  readonly ledger: AccountLedgerResponse | null | undefined;
}): React.JSX.Element | null {
  // Absent until the balance has been recorded once. Four of the five terms
  // would be a total that does not add up, which is worse than no total.
  if (!ledger) return null;

  return (
    <section
      data-testid="account-ledger"
      aria-labelledby="account-ledger-heading"
      className="flex min-w-0 flex-col overflow-hidden rounded-panel border border-border-subtle bg-surface-default"
    >
      <div className={`${PANEL_HEADER} justify-between gap-3`}>
        <h2 id="account-ledger-heading" className={PANEL_TITLE}>
          Итог по счёту
        </h2>
        {/* The moment the account started working, not a period. Every other
            block on this page moves with the period control and this one does
            not, so it names its own beginning instead. */}
        <span className="text-helper-sm text-text-muted" data-testid="ledger-since">
          {ledger.since === null ? "за всё время" : `с ${day(ledger.since)}`}
        </span>
      </div>

      <div className="flex flex-col gap-2 p-3">
        {/* What the account already held when the strategy started. Funding
            it, drawing it down and funding it again was the owner setting it
            up, not the strategy's history — and listing those movements below
            would report withdrawals against a working period that had none. */}
        <Line
          label="На старте"
          testId="ledger-opening"
          value={ledger.opening}
          note={ledger.since === null ? undefined : `было на счёте ${day(ledger.since)}`}
        />
        {/* Movements, not results. The result scale means money the strategy
            made or lost; a deposit painted green would say the account earned
            five thousand. The sign carries the direction, as it does in the
            movements list. */}
        <Line
          label="Пополнено с тех пор"
          testId="ledger-deposited"
          value={ledger.deposited}
          note={count(ledger.deposits, "пополнение", "пополнения", "пополнений")}
          signed
        />
        <Line
          label="Выведено с тех пор"
          testId="ledger-withdrawn"
          value={ledger.withdrawn}
          note={
            ledger.withdrawals === 0
              ? "выводов не было"
              : count(ledger.withdrawals, "вывод", "вывода", "выводов")
          }
          signed
        />
        <Line
          label="Вложено владельцем"
          testId="ledger-invested"
          value={ledger.invested}
          note="старт плюс движения с тех пор"
        />
        <Line
          label="Заработал Skryptos"
          testId="ledger-realised"
          value={ledger.realised}
          note={count(ledger.cycles, "закрытый цикл", "закрытых цикла", "закрытых циклов")}
          signed
          tone="result"
        />
        {/* What the venue closed and no cycle of ours claims: the owner
            closing a position by hand, three times between 20 and 25 August.
            The money is in the balance and in none of our records, so until it
            was named it read as a hole rather than as a result somebody chose
            to take. Absent, not zero, while the venue journal has not been
            collected — zero would be a claim nobody measured. */}
        {ledger.manual !== null && (
          <Line
            label="Ручные сделки"
            testId="ledger-manual"
            value={ledger.manual}
            note="закрыто не ботом"
            signed
            tone="result"
          />
        )}
        {/* The part of trading the results do not carry. A trade's result on
            the venue is not what the trade did to the wallet; our cycles stay
            on the venue's basis, because that is what makes them equal its
            figure, so the difference is stated rather than hidden inside
            them. */}
        {ledger.costs !== null && (
          <Line
            label="Комиссии сверх результата"
            testId="ledger-costs"
            value={ledger.costs}
            note="не входят в результат сделок"
            signed
            tone="result"
          />
        )}
        {/* Its own line, beside the realised result and not inside it. A
            perpetual settles funding against the position every eight hours;
            the venue keeps it out of a trade's result and so do we, which is
            why our cycles match the venue's to four decimals. Until it was
            named here it landed in "не объяснено записями", where real income
            read as a hole in the records. Always shown, even at zero: a term
            that appears and disappears is a column a reader cannot check. */}
        <Line
          label="Фандинг"
          testId="ledger-funding"
          value={ledger.funding}
          note="расчёты по перпетуалам, каждые 8 часов"
          signed
          tone="result"
        />
        <Line
          label="Открытые позиции"
          testId="ledger-unrealised"
          value={ledger.unrealised}
          note="оценка биржи, ещё не зафиксировано"
          signed
          tone="result"
        />
        {/* Named rather than folded into the position above.
            The position is measured — the venue values it — and the balance
            carries history the record does not hold: this account traded for
            nineteen days before its first cycle was written down. Adding that
            difference into a measured figure would make it carry an unmeasured
            one, and the column would still add up, which is exactly why
            nobody would notice. */}
        {!isZero(toPlaces(ledger.unexplained)) && (
          <Line
            label="Не объяснено записями"
            testId="ledger-unexplained"
            value={ledger.unexplained}
            note="движения до начала записи"
            signed
          />
        )}
        <Line
          label="Счёт сейчас"
          testId="ledger-equity"
          value={ledger.equity}
          tone="total"
        />
      </div>
    </section>
  );
}
